import {
  DateInput,
  NumberInput,
  RadioGroupInput,
  SwitchInput,
  TextAreaInput,
  TextInput,
} from 'corso-admin-ui';
import { maxPolicyEligibilityDays } from 'corso-types';
import { Controller } from 'react-hook-form';
import { getNullableNumberValue, handleLifetimeFieldValue } from '~/utils/form';
import { useAutomationFormContext } from '../hooks/useAutomationForm';

const resolutionKind = {
  returnableUntil: 'Returnable Until Date',
  eligibilityDays: 'Eligibility Window Days',
} as const;

// ? maybe this could leverage `Intl.PluralRules` // TODO better name
function zeroNone(
  {
    zero,
    none,
  }: {
    zero: string;
    none: string;
  },
  value: number | null,
) {
  if (value === null) return none;
  if (value === 0) return zero;

  // ? maybe support `one` value lookup, among other ordinals via `Partial`
  return undefined;
}

export default function EventModifyResolutionWindow() {
  const { control, watch } = useAutomationFormContext();

  const selectedResolutionKind = watch('event.params.kind');
  const isIneligibleWarrantyClaimAllowed = watch(
    'event.params.isIneligibleWarrantyClaimAllowed',
  );

  return (
    <>
      <Controller
        control={control}
        name="event.params.kind"
        defaultValue="returnableUntil"
        render={({ field, fieldState }) => (
          <RadioGroupInput
            label="Kind" // TODO better text
            labelVisuallyHidden
            id="kind"
            options={resolutionKind}
            value={field.value}
            // TODO reset other parameter fields when changing kind
            onChange={field.onChange}
            error={fieldState.error?.message}
          />
        )}
      />
      {/* // TODO evaluate if there's a nice way to create all of these controlled inputs programmatically for each type */}
      {/* // * all these inputs are controlled to aid with the discerning error messages from discriminated union merging in the form */}
      {selectedResolutionKind === 'returnableUntil' ? (
        // TODO `details` representing these as UTC dates
        <>
          <Controller
            control={control}
            name="event.params.refund"
            render={({ field, fieldState }) => (
              <DateInput
                id="refund"
                label="Refund"
                value={(field.value as string | undefined) ?? null} // ! EXPLICIT TYPE ASSERTION // TODO resolve and remove
                onChange={field.onChange}
                error={fieldState.error?.message}
                details={
                  !(field.value as string | undefined) &&
                  'Using Store Defaults for Refunds'
                } // ! EXPLICIT TYPE ASSERTION // TODO resolve and remove
              />
            )}
          />
          <Controller
            control={control}
            name="event.params.giftCard"
            render={({ field, fieldState }) => (
              <DateInput
                id="gift-card"
                label="Gift Card"
                value={(field.value as string | undefined) ?? null} // ! EXPLICIT TYPE ASSERTION // TODO resolve and remove
                onChange={field.onChange}
                error={fieldState.error?.message}
                details={
                  !(field.value as string | undefined) &&
                  'Using Store Defaults for Gift Cards'
                } // ! EXPLICIT TYPE ASSERTION // TODO resolve and remove
              />
            )}
          />
          <Controller
            control={control}
            name="event.params.exchange"
            render={({ field, fieldState }) => (
              <DateInput
                id="variant-exchange"
                label="Variant Exchange"
                value={(field.value as string | undefined) ?? null} // ! EXPLICIT TYPE ASSERTION // TODO resolve and remove
                onChange={field.onChange}
                error={fieldState.error?.message}
                details={
                  !(field.value as string | undefined) &&
                  'Using Store Defaults for Variant Exchanges'
                } // ! EXPLICIT TYPE ASSERTION // TODO resolve and remove
              />
            )}
          />
          <Controller
            control={control}
            name="event.params.warrantyReview"
            render={({ field, fieldState }) => (
              <DateInput
                id="warranty-review"
                label="Warranty Review"
                value={(field.value as string | undefined) ?? null} // ! EXPLICIT TYPE ASSERTION // TODO resolve and remove
                onChange={field.onChange}
                error={fieldState.error?.message}
                details={
                  !(field.value as string | undefined) &&
                  'Using Store Defaults for Warranty Reviews'
                } // ! EXPLICIT TYPE ASSERTION // TODO resolve and remove
              />
            )}
          />
        </>
      ) : (
        <>
          <Controller
            control={control}
            name="event.params.refund"
            render={({ field, fieldState }) => (
              <NumberInput
                id="refund"
                label="Refund"
                addon={{ insideEnd: 'days' }}
                value={(field.value as number | undefined) ?? null} // ! EXPLICIT TYPE ASSERTION // TODO resolve and remove
                onChange={(event) =>
                  field.onChange(getNullableNumberValue(event))
                }
                error={fieldState.error?.message}
                details={zeroNone(
                  {
                    zero: 'Refunds Not Offered',
                    none: 'Using Store Defaults for Refunds',
                  },
                  (field.value as number | undefined) ?? null, // ! EXPLICIT TYPE ASSERTION // TODO resolve and remove
                )}
              />
            )}
          />
          <Controller
            control={control}
            name="event.params.giftCard"
            render={({ field, fieldState }) => (
              <NumberInput
                id="gift-card"
                label="Gift Card"
                addon={{ insideEnd: 'days' }}
                value={(field.value as number | undefined) ?? null} // ! EXPLICIT TYPE ASSERTION // TODO resolve and remove
                onChange={(event) =>
                  field.onChange(getNullableNumberValue(event))
                }
                error={fieldState.error?.message}
                details={zeroNone(
                  {
                    zero: 'Gift Cards Not Offered',
                    none: 'Using Store Defaults for Gift Cards',
                  },
                  (field.value as number | undefined) ?? null, // ! EXPLICIT TYPE ASSERTION // TODO resolve and remove
                )}
              />
            )}
          />
          <Controller
            control={control}
            name="event.params.exchange"
            render={({ field, fieldState }) => (
              <NumberInput
                id="variant-exchange"
                label="Variant Exchange"
                addon={{ insideEnd: 'days' }}
                value={(field.value as number | undefined) ?? null} // ! EXPLICIT TYPE ASSERTION // TODO resolve and remove
                onChange={(event) =>
                  field.onChange(getNullableNumberValue(event))
                }
                error={fieldState.error?.message}
                details={zeroNone(
                  {
                    zero: 'Variant Exchanges Not Offered',
                    none: 'Using Store Defaults for Variant Exchanges',
                  },
                  (field.value as number | undefined) ?? null, // ! EXPLICIT TYPE ASSERTION // TODO resolve and remove
                )}
              />
            )}
          />
          <Controller
            control={control}
            name="event.params.warrantyReview"
            render={({ field, fieldState }) => (
              <NumberInput
                id="warranty-review"
                label="Warranty Review"
                addon={{
                  insideEnd:
                    field.value === maxPolicyEligibilityDays ? null : 'Days',
                  outsideStart: (
                    <div className="mr-4 flex items-center">
                      <SwitchInput
                        label="Lifetime"
                        id="lifetime-warranty"
                        checked={field.value === maxPolicyEligibilityDays}
                        onChange={(e) => {
                          if (e.target.checked) {
                            field.onChange(maxPolicyEligibilityDays);
                          } else {
                            field.onChange(null);
                          }
                        }}
                      />
                    </div>
                  ),
                }}
                value={handleLifetimeFieldValue(field.value)}
                onChange={(event) =>
                  field.onChange(getNullableNumberValue(event))
                }
                disabled={field.value === maxPolicyEligibilityDays}
                error={fieldState.error?.message}
                details={zeroNone(
                  {
                    zero: 'Warranty Reviews Not Offered',
                    none: 'Using Store Defaults for Warranty Review',
                  },
                  (field.value as number | undefined) ?? null, // ! EXPLICIT TYPE ASSERTION // TODO resolve and remove
                )}
              />
            )}
          />
        </>
      )}
      <Controller
        control={control}
        name="event.params.ineligibleLineItemFootnote"
        render={({ field, fieldState }) => (
          <TextInput
            id="ineligible-line-item-footnote"
            label="Ineligible Line Item Footnote"
            details="The note to display when a line item is made ineligible by this rule, e.g. 'Final Sale'."
            {...field}
            error={fieldState.error?.message}
          />
        )}
      />
      <Controller
        control={control}
        name="event.params.isIneligibleWarrantyClaimAllowed"
        render={({ field, fieldState }) => (
          <SwitchInput
            id="ineligible-warranty-claim-allowed"
            label="Allow Warranty Claims Outside Eligibility Period"
            details="Let customers still submit a warranty claim after the eligibility period has passed."
            checked={field.value}
            onChange={field.onChange}
            error={fieldState.error?.message}
          />
        )}
      />
      {isIneligibleWarrantyClaimAllowed && (
        <>
          <Controller
            control={control}
            name="event.params.ineligibleWarrantyClaimTitle"
            shouldUnregister
            render={({ field, fieldState }) => (
              <TextInput
                id="ineligible-warranty-claim-title"
                label="Outside Eligibility Title"
                details="The heading shown above the disclaimer."
                placeholder="This item's warranty window has closed"
                {...field}
                error={fieldState.error?.message}
              />
            )}
          />
          <Controller
            control={control}
            name="event.params.ineligibleWarrantyClaimDisclaimer"
            shouldUnregister
            render={({ field, fieldState }) => (
              <TextAreaInput
                id="ineligible-warranty-claim-disclaimer"
                label="Outside Eligibility Disclaimer"
                details="Shown to the customer after they select an item whose warranty window has passed."
                placeholder="It looks like this item's window has expired. We'd still like to help, please continue and we'll do our best to take care of you."
                {...field}
                error={fieldState.error?.message}
              />
            )}
          />
        </>
      )}
    </>
  );
}
